import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import { useBranch } from './../layouts/BranchContext';
import { useNavigate } from "react-router-dom";

import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  useTheme,
  alpha,
  Button,
  Tooltip as MuiTooltip,
} from "@mui/material";

import {
  FaPhone,
  FaUserPlus,
  FaMoneyBill,
  FaUsers,
  FaBook,
  FaCalendarCheck,
  FaChartLine,
  FaWallet,
  FaArrowUp,
  FaArrowDown,
  FaCheckCircle,
  FaExclamationCircle,
  FaClock,
  FaBuilding,
  FaChartBar,
  FaUserClock,
  FaGraduationCap,
  FaFileInvoice,
  FaUserTie,
  FaCheck,
  FaTimes,
  FaUserCheck,
} from "react-icons/fa";

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
} from "recharts";

import axios from "axios";

// ======================================================
// HELPERS
// ======================================================

const formatCurrency = (value) => {
  const number = Number(value) || 0;

  return `₹${number.toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  })}`;
};

const formatNumber = (value) => {
  return (Number(value) || 0).toLocaleString("en-IN");
};

const getGreeting = () => {
  const hour = new Date().getHours();

  if (hour < 12) return "Good Morning";
  if (hour < 17) return "Good Afternoon";

  return "Good Evening";
};

const truncateText = (text, maxLength = 16) => {
  if (!text) return "Other";

  return text.length > maxLength
    ? `${text.slice(0, maxLength)}...`
    : text;
};

const formatMonthYear = (month, year) =>
  new Date(Number(year), Number(month) - 1).toLocaleString(
    "en-IN",
    { month: "short", year: "numeric" }
  );

// ======================================================
// KPI CARD
// ======================================================

const KPICard = ({
  title,
  value,
  subtitle,
  icon,
  color,
  trend,
  trendType = "positive",
  theme,
}) => {
  const isPositive = trendType === "positive";

  const trendColor = isPositive
    ? "#16a34a"
    : "#dc2626";

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: "16px",
        border: "1px solid",
        borderColor: theme.palette.divider,
        backgroundColor: theme.palette.background.paper,
        height: "100%",
        minHeight: 170,
        boxSizing: "border-box",
        transition: "all 0.2s ease",

        "&:hover": {
          transform: "translateY(-3px)",
          borderColor: alpha(color, 0.55),
          boxShadow:
            theme.palette.mode === "dark"
              ? "0 10px 30px rgba(0,0,0,0.25)"
              : "0 10px 30px rgba(15,23,42,0.08)",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <Typography
          sx={{
            fontSize: "0.75rem",
            fontWeight: 800,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            color:
              theme.palette.mode === "dark"
                ? "#cbd5e1"
                : "#64748b",
          }}
        >
          {title}
        </Typography>

        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: "12px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color,
            backgroundColor: alpha(color, 0.1),
          }}
        >
          {icon}
        </Box>
      </Box>

      <Typography
        sx={{
          mt: 2,
          fontSize: {
            xs: "1.5rem",
            md: "1.8rem",
          },
          fontWeight: 800,
          letterSpacing: "-0.04em",
          color: theme.palette.text.primary,
        }}
      >
        {value}
      </Typography>

      <Box
        sx={{
          mt: 1.2,
          minHeight: 28,
          display: "flex",
          alignItems: "center",
        }}
      >
        {trend ? (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.6,
              px: 1,
              py: 0.45,
              borderRadius: "8px",
              color: trendColor,
              backgroundColor: alpha(
                trendColor,
                0.1
              ),
              fontSize: "0.72rem",
              fontWeight: 800,
              lineHeight: 1,
            }}
          >
            {isPositive ? (
              <FaArrowUp size={10} />
            ) : (
              <FaArrowDown size={10} />
            )}

            {trend}
          </Box>
        ) : (
          <Box sx={{ height: 28 }} />
        )}
      </Box>

      <Typography
        sx={{
          mt: 0.9,
          fontSize: "0.72rem",
          color: theme.palette.text.primary,
        }}
      >
        {subtitle}
      </Typography>
    </Paper>
  );
};

// ======================================================
// SECTION HEADER
// ======================================================

const SectionHeader = ({
  title,
  subtitle,
  theme,
}) => {
  return (
    <Box sx={{ mb: 2.5 }}>
      <Typography
        sx={{
          fontSize: "1.05rem",
          fontWeight: 800,
          color: theme.palette.text.primary,
        }}
      >
        {title}
      </Typography>

      {subtitle && (
        <Typography
          sx={{
            mt: 0.4,
            fontSize: "0.75rem",
            color: theme.palette.text.primary,
          }}
        >
          {subtitle}
        </Typography>
      )}
    </Box>
  );
};

// ======================================================
// CHART TOOLTIP
// ======================================================

const ChartTooltip = ({
  active,
  payload,
  label,
}) => {
  const theme = useTheme();

  if (!active || !payload?.length) {
    return null;
  }

  return (
    <Paper
      elevation={4}
      sx={{
        px: 1.5,
        py: 1.2,
        borderRadius: "10px",
        border: "1px solid",
        borderColor: theme.palette.divider,
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <Typography
        sx={{
          fontSize: "0.72rem",
          fontWeight: 700,
          mb: 0.7,
          color: theme.palette.text.primary,
        }}
      >
        {label}
      </Typography>

      {payload.map((item) => (
        <Typography
          key={item.dataKey}
          sx={{
            fontSize: "0.75rem",
            fontWeight: 600,
            color: theme.palette.text.primary,
          }}
        >
          {item.name}: {formatCurrency(item.value)}
        </Typography>
      ))}
    </Paper>
  );
};

// ======================================================
// DASHBOARD
// ======================================================

const Dashboard = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  const BASE_URL =
    process.env.REACT_APP_API_BASE_URL;

  // ======================================================
  // USER
  // ======================================================

  const user = useMemo(() => {
    try {
      return (
        JSON.parse(
          sessionStorage.getItem("user")
        ) || {}
      );
    } catch {
      return {};
    }
  }, []);

  const token =
    localStorage.getItem("accessToken");

  const organization_id =
    user?.organization_id;

  const branch_id =
    user?.branch_id;

  const employeeId =
    user?.employee_details?.employee_id;

  const admissionId =
    user?.admission_details?.admission_id;

  const role =
    user?.role?.toLowerCase();

  const isTeacher =
    role === "teacher";

  const isStudent =
    role === "student";

  const isAdmin =
    !isTeacher &&
    !isStudent &&
    (
      user?.is_admin ||
      user?.is_main_branch_admin ||
      user?.is_branch_super_admin
    );

  // ======================================================
  // STATE
  // ======================================================

  const [
    dashboardData,
    setDashboardData,
  ] = useState({});

  const { selectedBranchId, branchList, isMainBranchAdmin } = useBranch();

  const [
    libraryExpenseFinance,
    setLibraryExpenseFinance,
  ] = useState(null);

  const [
    libraryExpenseCategory,
    setLibraryExpenseCategory,
  ] = useState(null);

  const [
    libraryExpenseCategoryList,
    setLibraryExpenseCategoryList,
  ] = useState(null);

  const [
    libraryExpenseBranch,
    setLibraryExpenseBranch,
  ] = useState(null);

  // ======================================================
  // NEW STATE
  // ONLY FOR BRANCH-WISE TABLE DATA
  // ======================================================

  const [
    libraryExpenseBranchList,
    setLibraryExpenseBranchList,
  ] = useState([]);

  const [
    branches,
    setBranches,
  ] = useState([]);

  const [
    selectedBranch,
    setSelectedBranch,
  ] = useState("");

  const [
    selectedMonth,
    setSelectedMonth,
  ] = useState(
    new Date().getMonth()
  );

  const [
    selectedYear,
    setSelectedYear,
  ] = useState(
    new Date().getFullYear()
  );

  const [
    loading,
    setLoading,
  ] = useState(true);

 const buildUrl = useCallback(
  (endpoint) => {
    const params = new URLSearchParams();
    const activeBranchId = selectedBranchId === "" ? null : selectedBranchId;

    // ADMIN
    if (isAdmin) {
      if (organization_id) {
        params.append("organization_id", organization_id);
      }
      if (activeBranchId !== null) {
        params.append("branch_id", activeBranchId);
      }
      return params.toString() ? `${endpoint}?${params.toString()}` : endpoint;
    }

    // TEACHER
    if (isTeacher) {
      if (organization_id) {
        params.append("organization_id", organization_id);
      }
      if (branch_id) {
        params.append("branch_id", branch_id);
      }
      if (employeeId) {
        params.append("employee_id", employeeId);
      }
      return params.toString() ? `${endpoint}?${params.toString()}` : endpoint;
    }

    // STUDENT
    if (isStudent) {
      if (organization_id) {
        params.append("organization_id", organization_id);
      }
      if (branch_id) {
        params.append("branch_id", branch_id);
      }
      if (admissionId) {
        params.append("admission_id", admissionId);
      }
      return params.toString() ? `${endpoint}?${params.toString()}` : endpoint;
    }

    // NORMAL USER
    if (organization_id) {
      params.append("organization_id", organization_id);
    }
    if (branch_id) {
      params.append("branch_id", branch_id);
    }
    return params.toString() ? `${endpoint}?${params.toString()}` : endpoint;
  },
  [selectedBranchId, organization_id, branch_id, employeeId, admissionId, isTeacher, isStudent, isAdmin]
);

const buildExpenseParams = useCallback(() => {
  const params = {
    organization: organization_id,
  };
  if (selectedBranchId !== "") {
    params.branch = selectedBranchId;
  } else if (!isAdmin && branch_id) {
    params.branch = branch_id;
  }
  return params;
}, [organization_id, selectedBranchId, branch_id, isAdmin]);


// ======================================================
  // LOAD MAIN DASHBOARD
  // ======================================================

  const [hrDashboardData, setHrDashboardData] = useState(null);

 const loadMainDashboardCounts = useCallback(async () => {
  if (!token || !organization_id) return;
  const headers = {
    Authorization: `Bearer ${token}`,
    Accept: "application/json",
  };

  const endpoints = {
    enquiry: "/api/student_details/enquiry/dashboard-count/",
    followup: "/api/student_details/followup/dashboard-count/",
    admission: "/api/admission/admission/dashboard-count/",
    payment: "/api/fee_details/fee-deposit/dashboard-count/",
    course: "/api/course/course/dashboard-count/",
    employee: "/api/staff/employee/dashboard-count/",
    studentAttendance: "/api/admission/attendance/dashboard-count/",
  };

  const requests = Object.entries(endpoints).map(([key, endpoint]) => {
    const params = {
      organization_id,
    };
    if (selectedBranchId !== "") {
      params.branch_id = selectedBranchId;
    } else if (branch_id && (!isAdmin || role === "branch admin" || role === "branch_admin" || role === "branch-admin")) {
      params.branch_id = branch_id;
    }
    return axios
      .get(`${BASE_URL}${endpoint}`, {
        params,
        headers,
      })
      .then((response) => ({
        key,
        status: "fulfilled",
        value: response,
      }))
      .catch((error) => ({
        key,
        status: "rejected",
        reason: error,
      }));
  });

  const results = await Promise.all(requests);
  const nextDashboardData = {};
  results.forEach((result) => {
    if (result.status === "fulfilled") {
      // Keep the { status, value } wrapper because the dashboard mappings
      // read response data through payment.value.data, course.value.data, etc.
      nextDashboardData[result.key] = result;
    } else {
      console.error(`DASHBOARD ${result.key.toUpperCase()} API ERROR:`, result.reason?.response?.data || result.reason);
      nextDashboardData[result.key] = result;
    }
  });
  setDashboardData(nextDashboardData);
}, [BASE_URL, token, organization_id, selectedBranchId, branch_id, isAdmin, role]);

 const loadDashboard = useCallback(async () => {
  if (!token || !organization_id) return;
  try {
    const params = {
      organization: organization_id,
      month: selectedMonth + 1,
      year: selectedYear,
    };
    if (selectedBranchId !== "") {
      params.branch = selectedBranchId;
    }
    const response = await axios.get(`${BASE_URL}/api/hr/dashboard/admin/`, {
      params,
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      },
    });
    console.log("HR ADMIN DASHBOARD RESPONSE:", response.data);
    const hrData = response.data?.data || response.data || {};
    console.log("HR LEAVE OVERVIEW:", hrData?.leave_overview);
    console.log("HR ATTENDANCE OVERVIEW:", hrData?.attendance_overview);
    setHrDashboardData(hrData);
  } catch (error) {
    console.error("HR DASHBOARD ERROR:", error.response?.data || error);
    setHrDashboardData(null);
  }
}, [BASE_URL, token, organization_id, selectedBranchId, selectedMonth, selectedYear]);

  // ======================================================
  // LOAD EXPENSE DASHBOARD
  // ======================================================

  const loadExpenseDashboard = useCallback(async () => {
  if (!token || !organization_id) {
    return;
  }
  try {
    const params = buildExpenseParams();
    const headers = {
      Authorization: `Bearer ${token}`,
    };

    // FINANCE + CATEGORY
    const results = await Promise.allSettled([
      axios.get(`${BASE_URL}/api/library/expense/dashboard/finance/`, {
        params,
        headers,
      }),
      axios.get(`${BASE_URL}/api/library/expense/dashboard/category/`, {
        params,
        headers,
      }),
      axios.get(`${BASE_URL}/api/library/expense-category/list/`, {
        params,
        headers,
      }),
    ]);

    setLibraryExpenseFinance(
      results[0].status === "fulfilled"
        ? results[0].value?.data?.data || results[0].value?.data
        : null
    );
    setLibraryExpenseCategory(
      results[1].status === "fulfilled"
        ? results[1].value?.data?.data || results[1].value?.data
        : null
    );
    setLibraryExpenseCategoryList(
      results[2].status === "fulfilled"
        ? results[2].value?.data?.data || results[2].value?.data
        : null
    );

    // BRANCH EXPENSE
    if (isAdmin && selectedBranchId === "") {
      if (!branchList.length) {
        setLibraryExpenseBranch(null);
        setLibraryExpenseBranchList([]);
        return;
      }

      const branchRequests = branchList.map((branch) =>
        axios.get(`${BASE_URL}/api/library/expense/dashboard/branch/`, {
          params: {
            organization: organization_id,
            branch: branch.id,
          },
          headers,
        })
      );
      const branchResults = await Promise.allSettled(branchRequests);
      const branchWiseData = branchResults.map((result, index) => {
        const currentBranch = branchList[index];
        if (result.status !== "fulfilled") {
          console.error(`BRANCH EXPENSE ERROR - Branch ${currentBranch?.id}:`, result.reason?.response?.data || result.reason);
          return {
            branch_id: currentBranch?.id,
            branch_name: currentBranch?.name || `Branch ${currentBranch?.id}`,
            total_expenses: 0,
            approved_amount: 0,
            pending: 0,
            top_category: "—",
            status: "No Data",
          };
        }
        const apiData = result.value?.data?.data || result.value?.data || {};
        return {
          branch_id: currentBranch?.id,
          branch_name: currentBranch?.name || apiData?.branch?.name || apiData?.branch || `Branch ${currentBranch?.id}`,
          total_expenses: Number(apiData?.total_expenses ?? apiData?.total_expense_count ?? apiData?.expense_count ?? 0) || 0,
          approved_amount: Number(apiData?.approved_amount ?? apiData?.total_amount ?? apiData?.total_expense_amount ?? apiData?.expense_amount ?? 0) || 0,
          pending: Number(apiData?.pending_expenses ?? apiData?.pending ?? apiData?.pending_count ?? 0) || 0,
          top_category: apiData?.top_category || apiData?.category_name || "—",
          status: apiData?.status || "Active",
        };
      });
      setLibraryExpenseBranch(null);
      setLibraryExpenseBranchList(branchWiseData);
    } else {
      const branchExpense = await axios.get(`${BASE_URL}/api/library/expense/dashboard/branch/`, {
        params: {
          organization: organization_id,
          branch: selectedBranchId,
        },
        headers,
      });
      const apiData = branchExpense.data?.data || branchExpense.data || {};
      const selectedBranchObject = branchList.find((branch) => String(branch.id) === String(selectedBranchId));
      setLibraryExpenseBranch(apiData);
      setLibraryExpenseBranchList([
        {
          branch_id: selectedBranchObject?.id || selectedBranchId,
          branch_name: selectedBranchObject?.name || apiData?.branch?.name || apiData?.branch || "Selected Branch",
          total_expenses: Number(apiData?.total_expenses ?? apiData?.total_expense_count ?? 0) || 0,
          approved_amount: Number(apiData?.approved_amount ?? apiData?.total_amount ?? 0) || 0,
          pending: Number(apiData?.pending_expenses ?? apiData?.pending ?? apiData?.pending_count ?? 0) || 0,
          top_category: apiData?.top_category || apiData?.category_name || "—",
          status: apiData?.status || "Active",
        },
      ]);
    }
  } catch (error) {
    console.error("EXPENSE DASHBOARD ERROR:", error.response?.data || error);
    setLibraryExpenseFinance(null);
    setLibraryExpenseCategory(null);
    setLibraryExpenseCategoryList(null);
    setLibraryExpenseBranch(null);
    setLibraryExpenseBranchList([]);
  }
}, [BASE_URL, token, organization_id, buildExpenseParams, isAdmin, selectedBranchId, branchList]);
  // ======================================================
  // LOAD ALL DATA
  // ======================================================

  useEffect(() => {
    if (
      !token ||
      !organization_id
    ) {
      setLoading(false);
      return;
    }

    let mounted = true;

    const loadAllData =
      async () => {
        if (mounted) {
          setLoading(true);
        }

        try {
          await Promise.all([
            loadMainDashboardCounts(),
            loadDashboard(),
            loadExpenseDashboard(),
          ]);
        } catch (error) {
          console.error(
            "LOAD ALL DATA ERROR:",
            error
          );
        } finally {
          if (mounted) {
            setLoading(false);
          }
        }
      };

    loadAllData();

    return () => {
      mounted = false;
    };
  }, [
    token,
    organization_id,
    selectedBranch,
    loadMainDashboardCounts,
    loadDashboard,
    loadExpenseDashboard,
  ]);

  // ======================================================
  // RESPONSE SHORTCUTS
  // ======================================================

  const enquiry =
    dashboardData.enquiry;

  const followup =
    dashboardData.followup;

  const admission =
    dashboardData.admission;

  const payment =
    dashboardData.payment;

  const course =
    dashboardData.course;

  const employee =
    dashboardData.employee;

  const studentAttendance =
    dashboardData.studentAttendance;

  // ======================================================
  // HR DASHBOARD VALUES
  // ======================================================

  const hrEmployeeOverview = hrDashboardData?.employee_overview || {};
  const hrAttendanceOverview = hrDashboardData?.attendance_overview || {};
  const hrPayrollOverview = hrDashboardData?.payroll_overview || {};
  const hrTodayAttendance = hrAttendanceOverview?.today || {};
  const hrMonthAttendance = hrAttendanceOverview?.selected_month || {};

  const hrTotalEmployees = Number(hrEmployeeOverview?.total_employees) || 0;
  const hrActiveEmployees = Number(hrEmployeeOverview?.active_employees) || 0;
  const hrInactiveEmployees = Number(hrEmployeeOverview?.inactive_employees) || 0;
  const hrNewJoiners = Number(hrEmployeeOverview?.new_joiners) || 0;

  const hrTodayPresent = Number(hrTodayAttendance?.present) || 0;
  const hrTodayAbsent = Number(hrTodayAttendance?.absent) || 0;
  const hrTodayHalfDay = Number(hrTodayAttendance?.half_day) || 0;
  const hrTodayOnLeave = Number(hrTodayAttendance?.on_leave) || 0;
  const hrTodayMarked = Number(hrTodayAttendance?.marked) || 0;
  const hrTodayAttendancePercentage =
    Number(hrTodayAttendance?.attendance_percentage) || 0;

  const hrMonthPresent = Number(hrMonthAttendance?.present) || 0;
  const hrMonthAbsent = Number(hrMonthAttendance?.absent) || 0;
  const hrMonthHalfDay = Number(hrMonthAttendance?.half_day) || 0;
  const hrMonthOnLeave = Number(hrMonthAttendance?.on_leave) || 0;
  const hrMonthAttendancePercentage =
    Number(hrMonthAttendance?.attendance_percentage) || 0;

  const hrLeaveOverview =
    hrDashboardData?.leave_overview || {};

  const hrPendingLeaves =
    Number(hrLeaveOverview?.pending) || 0;
  const hrApprovedLeaves =
    Number(hrLeaveOverview?.approved) || 0;
  const hrRejectedLeaves =
    Number(hrDashboardData?.leave_overview?.rejected) || 0;
  const hrCancelledLeaves =
    Number(hrDashboardData?.leave_overview?.cancelled) || 0;

  const hrPayrollCount =
    Number(hrPayrollOverview?.payroll_count) || 0;
  const hrPaidPayroll =
    Number(hrPayrollOverview?.paid_count) || 0;
  const hrPendingPayroll =
    Number(hrPayrollOverview?.pending_count) || 0;

  const hrGrossSalary =
    Number(hrPayrollOverview?.gross_salary) || 0;
  const hrDeductions =
    Number(hrPayrollOverview?.deductions) || 0;
  const hrAttendanceDeduction =
    Number(hrPayrollOverview?.attendance_deduction) || 0;
  const hrNetSalary =
    Number(hrPayrollOverview?.net_salary) || 0;

  const hrDepartmentData = useMemo(() => {
    if (!Array.isArray(hrDashboardData?.department_summary)) {
      return [];
    }

    const grouped = new Map();

    hrDashboardData.department_summary.forEach((item) => {
      const displayName = String(
        item?.department_name || "Unknown"
      ).trim();
      const key = displayName.toLowerCase();
      const count = Number(item?.employee_count) || 0;

      if (!grouped.has(key)) {
        grouped.set(key, { name: displayName, count: 0 });
      }

      grouped.get(key).count += count;
    });

    return Array.from(grouped.values());
  }, [hrDashboardData]);

  const hrDesignationData = useMemo(() => {
    if (!Array.isArray(hrDashboardData?.designation_summary)) {
      return [];
    }

    const grouped = new Map();

    hrDashboardData.designation_summary.forEach((item) => {
      const displayName = String(
        item?.designation_name || "Unknown"
      ).trim();
      const key = displayName.toLowerCase();
      const count = Number(item?.employee_count) || 0;

      if (!grouped.has(key)) {
        grouped.set(key, { name: displayName, count: 0 });
      }

      grouped.get(key).count += count;
    });

    return Array.from(grouped.values());
  }, [hrDashboardData]);

  const hrMonthlyPayrollTrend = useMemo(
    () =>
      Array.isArray(hrDashboardData?.monthly_payroll_trend)
        ? hrDashboardData.monthly_payroll_trend.map((item) => ({
            month: formatMonthYear(item?.month, item?.year),
            employeeCount: Number(item?.employee_count) || 0,
            gross: Number(item?.gross_salary) || 0,
            deductions: Number(item?.deductions) || 0,
            attendanceDeduction:
              Number(item?.attendance_deduction) || 0,
            net: Number(item?.net_salary) || 0,
          }))
        : [],
    [hrDashboardData]
  );

  const hrMonthlyAttendanceTrend = useMemo(
    () =>
      Array.isArray(hrDashboardData?.monthly_attendance_trend)
        ? hrDashboardData.monthly_attendance_trend.map((item) => ({
            month: formatMonthYear(item?.month, item?.year),
            present: Number(item?.present) || 0,
            absent: Number(item?.absent) || 0,
            halfDay: Number(item?.half_day) || 0,
            onLeave: Number(item?.on_leave) || 0,
            percentage:
              Number(item?.attendance_percentage) || 0,
          }))
        : [],
    [hrDashboardData]
  );

  const hrRecentLeaveApplications = useMemo(
    () =>
      Array.isArray(hrDashboardData?.recent_leave_applications)
        ? hrDashboardData.recent_leave_applications
        : [],
    [hrDashboardData]
  );

  const hrRecentEmployees = useMemo(
    () =>
      Array.isArray(hrDashboardData?.recent_employees)
        ? hrDashboardData.recent_employees
        : [],
    [hrDashboardData]
  );

  // ======================================================
  // COLLECTION VALUES
  // ======================================================

  const totalCollection =
    payment?.status === "fulfilled"
      ? Number(
          payment.value?.data
            ?.collection_amounts
            ?.total_collection
        ) || 0
      : 0;

  const monthlyCollection =
    payment?.status === "fulfilled"
      ? Number(
          payment.value?.data
            ?.collection_amounts
            ?.month_collection
        ) || 0
      : 0;

  const weekCollection =
    payment?.status === "fulfilled"
      ? Number(
          payment.value?.data
            ?.collection_amounts
            ?.week_collection
        ) || 0
      : 0;

  const todayCollection =
    payment?.status === "fulfilled"
      ? Number(
          payment.value?.data
            ?.collection_amounts
            ?.today_collection
        ) || 0
      : 0;

  // ======================================================
  // EXPENSE VALUES
  // ======================================================

  const totalExpense =
    Number(
      libraryExpenseFinance
        ?.approved_amount ??
        libraryExpenseFinance
          ?.total_amount ??
        libraryExpenseFinance
          ?.total_expenses_amount ??
        0
    ) || 0;

  const totalExpenseCount =
    Number(
      libraryExpenseBranch
        ?.total_expenses ??
        libraryExpenseFinance
          ?.total_expenses ??
        0
    ) || 0;

  const approvedExpenses =
    Number(
      libraryExpenseBranch
        ?.approved_expenses ??
        libraryExpenseFinance
          ?.approved_expenses ??
        0
    ) || 0;

  const pendingExpenses =
    Number(
      libraryExpenseBranch
        ?.pending_expenses ??
        libraryExpenseFinance
          ?.pending_expenses ??
        0
    ) || 0;

  const rejectedExpenses =
    Number(
      libraryExpenseBranch
        ?.rejected_expenses ??
        libraryExpenseFinance
          ?.rejected_expenses ??
        0
    ) || 0;

  const monthExpense =
    Number(
      libraryExpenseBranch
        ?.month_amount ??
        libraryExpenseFinance
          ?.month_amount ??
        libraryExpenseFinance
          ?.month_expense ??
        0
    ) || 0;

  const netBalance =
    totalCollection - totalExpense;

  // ======================================================
  // ADMISSION VALUES
  // ======================================================

  // ======================================================
  // ADMISSION VALUES
  // Supports the Axios response wrapper as well as a direct API payload.
  // API response: { success, filters, counts: { total_admissions, ... } }
  // ======================================================
  const admissionPayload =
    admission?.status === "fulfilled"
      ? admission?.value?.data
      : admission?.data || {};

  const admissionCounts =
    admissionPayload?.counts ||
    admissionPayload?.data?.counts ||
    admissionPayload?.result?.counts ||
    {};

  const totalStudents =
    Number(
      admissionCounts?.total_admissions ??
      admissionCounts?.total_students ??
      0
    ) || 0;

  const monthlyAdmissions =
    Number(
      admissionCounts?.month_admissions ??
      admissionCounts?.monthly_admissions ??
      admissionCounts?.new_admissions ??
      0
    ) || 0;

  // ======================================================
  // OTHER VALUES
  // ======================================================

  const totalEmployees =
    employee?.status === "fulfilled"
      ? Number(
          employee.value?.data
            ?.counts
            ?.total_employees
        ) || 0
      : 0;

  const todayEnquiries =
    enquiry?.status === "fulfilled"
      ? Number(
          enquiry.value?.data
            ?.counts
            ?.today_enquiries
        ) || 0
      : 0;

  const totalFollowups =
    followup?.status === "fulfilled"
      ? Number(
          followup.value?.data
            ?.counts
            ?.total_followups
        ) || 0
      : 0;

  const totalCourses =
    course?.status === "fulfilled"
      ? Number(
          course.value?.data
            ?.counts
            ?.total_courses
        ) || 0
      : 0;

  // ======================================================
  // ATTENDANCE
  // ======================================================

  const attendanceData =
    studentAttendance?.status ===
    "fulfilled"
      ? studentAttendance.value?.data
          ?.overall || {}
      : {};

  const totalAttendance =
    Number(
      attendanceData?.total_records
    ) || 0;

  const presentAttendance =
    Number(
      attendanceData?.present ??
        attendanceData?.present_count ??
        0
    ) || 0;

  const absentAttendance =
    Number(
      attendanceData?.absent ??
        attendanceData?.absent_count ??
        0
    ) || 0;

  const attendancePercentage =
    totalAttendance > 0
      ? Math.round(
          (presentAttendance /
            totalAttendance) *
            100
        )
      : 0;

  // ======================================================
  // FINANCIAL CHART
  // ======================================================

  const financialTrendData = useMemo(
    () => [
      {
        name: "This Month",
        collection: monthlyCollection,
        expense: monthExpense || totalExpense,
      },
      {
        name: "Total",
        collection: totalCollection,
        expense: totalExpense,
      },
    ],
    [
      monthlyCollection,
      monthExpense,
      totalExpense,
      totalCollection,
    ]
  );

  // ======================================================
  // CATEGORY DATA
  // ======================================================

  const categoryData =
    useMemo(() => {
      const expenseRows =
        libraryExpenseCategory?.data ||
        libraryExpenseCategory?.categories ||
        libraryExpenseCategory ||
        [];

      const masterRows =
        libraryExpenseCategoryList?.data ||
        libraryExpenseCategoryList?.categories ||
        libraryExpenseCategoryList ||
        [];

      const expenses = Array.isArray(expenseRows)
        ? expenseRows
        : [];

      const categories = Array.isArray(masterRows)
        ? masterRows
        : [];

      // Use the category master API as the source of truth for
      // the complete category list, then merge dashboard amounts.
      const expenseMap = new Map(
        expenses.map((item) => [
          String(
            item?.category_id ??
              item?.id ??
              item?.category
          ),
          Number(
            item?.total_amount ??
              item?.amount ??
              item?.total ??
              item?.approved_amount ??
              item?.value ??
              0
          ) || 0,
        ])
      );

      if (categories.length > 0) {
        return categories.map((item) => {
          const categoryId =
            item?.id ??
            item?.category_id;

          const name =
            item?.category_name ||
            item?.name ||
            item?.category ||
            "Other";

          const amountFromDashboard =
            categoryId != null
              ? expenseMap.get(String(categoryId))
              : undefined;

          // Fallback by name for APIs that don't expose a stable id.
          const amountByName =
            amountFromDashboard === undefined
              ? expenseMap.get(String(name))
              : amountFromDashboard;

          return {
            category_id: categoryId,
            name,
            value: Number(amountByName ?? 0) || 0,
          };
        });
      }

      // If the master list is unavailable, still show dashboard data.
      return expenses.map((item) => ({
        category_id:
          item?.category_id ?? item?.id,
        name:
          item?.category_name ||
          item?.name ||
          item?.category ||
          "Other",
        value:
          Number(
            item?.total_amount ??
              item?.amount ??
              item?.total ??
              item?.approved_amount ??
              item?.value ??
              0
          ) || 0,
      }));
    }, [
      libraryExpenseCategory,
      libraryExpenseCategoryList,
    ]);
  // ======================================================
  // EXPENSE FALLBACK DATA
  // ======================================================

  const expenseFallbackData =
    useMemo(() => {
      if (categoryData.length > 0) {
        return categoryData;
      }

      if (
        libraryExpenseBranch
          ?.top_category
      ) {
        return [
          {
            name:
              libraryExpenseBranch
                .top_category,

            value:
              Number(
                libraryExpenseBranch
                  ?.approved_amount ??
                  totalExpense ??
                  0
              ),
          },
        ];
      }

      return [];
    }, [
      categoryData,
      libraryExpenseBranch,
      totalExpense,
    ]);

  // ======================================================
  // BRANCH PERFORMANCE
  // ======================================================

  const branchPerformance = useMemo(() => {
    // ====================================================
    // ALL BRANCHES / SINGLE BRANCH
    // DATA IS ALREADY NORMALIZED
    // ====================================================

    if (
      Array.isArray(
        libraryExpenseBranchList
      ) &&
      libraryExpenseBranchList.length >
        0
    ) {
      return libraryExpenseBranchList;
    }

    // ====================================================
    // FALLBACK FOR ORIGINAL OBJECT RESPONSE
    // ====================================================

    if (!libraryExpenseBranch) {
      return [];
    }

    const branchObject =
      libraryExpenseBranch?.branch &&
      typeof libraryExpenseBranch.branch ===
        "object"
        ? libraryExpenseBranch.branch
        : null;

    let branchName =
      libraryExpenseBranch?.branch_name ||
      libraryExpenseBranch?.branchName ||
      branchObject?.name ||
      (
        typeof libraryExpenseBranch?.branch ===
        "string"
          ? libraryExpenseBranch.branch
          : ""
      ) ||
      libraryExpenseBranch?.name;

    if (
      !branchName &&
      selectedBranch !== ""
    ) {
      const selected =
        branches.find(
          (item) =>
            String(item.id) ===
            String(selectedBranch)
        );

      branchName =
        selected?.name;
    }

    if (
      !branchName &&
      branch_id
    ) {
      branchName =
        user?.branch_name ||
        "My Branch";
    }

    if (!branchName) {
      return [];
    }

    return [
      {
        branch_id:
          selectedBranch ||
          branch_id,

        branch_name:
          branchName,

        total_expenses:
          Number(
            libraryExpenseBranch
              ?.total_expenses ??
              libraryExpenseBranch
                ?.total_expense_count ??
              totalExpenseCount ??
              0
          ) || 0,

        approved_amount:
          Number(
            libraryExpenseBranch
              ?.approved_amount ??
              libraryExpenseBranch
                ?.total_amount ??
              totalExpense ??
              0
          ) || 0,

        pending:
          Number(
            libraryExpenseBranch
              ?.pending_expenses ??
              libraryExpenseBranch
                ?.pending ??
              pendingExpenses ??
              0
          ) || 0,

        top_category:
          libraryExpenseBranch
            ?.top_category ||
          libraryExpenseBranch
            ?.category_name ||
          "—",

        status:
          libraryExpenseBranch
            ?.status ||
          "Active",
      },
    ];
  }, [
    libraryExpenseBranchList,
    libraryExpenseBranch,
    selectedBranch,
    branches,
    branch_id,
    user,
    totalExpenseCount,
    totalExpense,
    pendingExpenses,
  ]);

  // ======================================================
  // RECENT ACTIVITIES
  // ======================================================

  const activities =
    useMemo(() => {
      const list = [];

      if (monthlyAdmissions > 0) {
        list.push({
          title:
            "New student admissions",

          description:
            `${monthlyAdmissions} new admissions added this month`,

          icon:
            <FaGraduationCap />,

          color:
            "#7c3aed",

          time:
            "This Month",
        });
      }

      if (todayCollection > 0) {
        list.push({
          title:
            "Payment collection received",

          description:
            `${formatCurrency(
              todayCollection
            )} collected today`,

          icon:
            <FaWallet />,

          color:
            "#059669",

          time:
            "Today",
        });
      }

      if (approvedExpenses > 0) {
        list.push({
          title:
            "Expenses approved",

          description:
            `${approvedExpenses} expenses approved`,

          icon:
            <FaCheckCircle />,

          color:
            "#059669",

          time:
            "Updated",
        });
      }

      if (pendingExpenses > 0) {
        list.push({
          title:
            "Expense requires approval",

          description:
            `${pendingExpenses} pending expenses`,

          icon:
            <FaExclamationCircle />,

          color:
            "#dc2626",

          time:
            "Pending",
        });
      }

      if (todayEnquiries > 0) {
        list.push({
          title:
            "New enquiries received",

          description:
            `${todayEnquiries} new enquiries today`,

          icon:
            <FaPhone />,

          color:
            "#2563eb",

          time:
            "Today",
        });
      }

      if (!list.length) {
        list.push({
          title:
            "No recent activity",

          description:
            "Dashboard activity will appear here",

          icon:
            <FaClock />,

          color:
            "#64748b",

          time:
            "—",
        });
      }

      return list.slice(0, 4);
    }, [
      monthlyAdmissions,
      todayCollection,
      approvedExpenses,
      pendingExpenses,
      todayEnquiries,
    ]);

  // ======================================================
  // MONTH LABEL
  // ======================================================

  const selectedMonthLabel =
    new Date(
      selectedYear,
      selectedMonth
    ).toLocaleString(
      "en-IN",
      {
        month: "long",
        year: "numeric",
      }
    );

  // ======================================================
  // LOADING
  // ======================================================

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
        }}
      >
        <CircularProgress size={42} />

        <Typography
          sx={{
            fontWeight: 700,
          }}
        >
          Loading dashboard...
        </Typography>
      </Box>
    );
  }

  // ======================================================
  // MAIN UI
  // ======================================================

  return (
    <Box
      sx={{
        maxWidth: 1600,
        mx: "auto",
        px: {
          xs: 1.5,
          sm: 2.5,
          md: 3,
        },
        py: 3,
        minHeight: "100vh",
      }}
    >
      {/* HEADER */}

      <Box
        sx={{
          display: "flex",
          justifyContent:
            "space-between",
          alignItems: {
            xs: "flex-start",
            md: "center",
          },
          flexDirection: {
            xs: "column",
            md: "row",
          },
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography
            sx={{
              mt: 0.8,
              fontSize: "1.05rem",
              fontWeight: 700,
              color:
                theme.palette.text.primary,
            }}
          >
            {getGreeting()},{" "}
            {user?.name ||
              user?.username ||
              user?.employee_name ||
              user?.employee_details
                ?.employee_name ||
              "User"}{" "}
            👋
          </Typography>

          <Typography
            sx={{
              mt: 0.3,
              fontSize: "0.78rem",
              color:
                theme.palette.text.primary,
            }}
          >
            Organization performance overview
          </Typography>
        </Box>

      </Box>

      {/* KPI CARDS */}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            md: "repeat(4, minmax(0, 1fr))",
          },
          gap: 2.5,
          mb: 3,
          width: "100%",
        }}
      >
        <KPICard
          title="Collection"
          value={formatCurrency(
            totalCollection
          )}
          subtitle="Total Collection"
          trend={
            monthlyCollection > 0
              ? `${formatCurrency(
                  monthlyCollection
                )} This Month`
              : null
          }
          trendType="positive"
          icon={<FaWallet size={17} />}
          color="#059669"
          theme={theme}
        />

        <KPICard
          title="Expense"
          value={formatCurrency(
            totalExpense
          )}
          subtitle={
            totalExpenseCount > 0
              ? `${totalExpenseCount} total expenses`
              : "Total Expenses"
          }
          trend={
            approvedExpenses > 0
              ? `${approvedExpenses} Approved`
              : pendingExpenses > 0
              ? `${pendingExpenses} Pending`
              : null
          }
          trendType={
            pendingExpenses > 0
              ? "negative"
              : "positive"
          }
          icon={
            <FaMoneyBill size={17} />
          }
          color="#ea580c"
          theme={theme}
        />

        <KPICard
          title="Net Balance"
          value={formatCurrency(
            netBalance
          )}
          subtitle="Current Balance"
          trend={
            netBalance >= 0
              ? "Positive"
              : "Negative"
          }
          trendType={
            netBalance >= 0
              ? "positive"
              : "negative"
          }
          icon={
            <FaChartLine size={17} />
          }
          color="#2563eb"
          theme={theme}
        />

        <KPICard
          title="Students"
          value={formatNumber(
            totalStudents
          )}
          subtitle="Total Admissions"
          trend={
            monthlyAdmissions > 0
              ? `${monthlyAdmissions} New`
              : null
          }
          trendType="positive"
          icon={<FaUsers size={17} />}
          color="#7c3aed"
          theme={theme}
        />
      </Box>

      {/* REQUIRES ATTENTION */}

      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 3,
          width: "100%",
          borderRadius: "16px",
          border: "1px solid",
          borderColor:
            theme.palette.divider,
          backgroundColor:
            theme.palette.background.paper,
          boxSizing: "border-box",
        }}
      >
        <SectionHeader
          title="Requires Attention"
          subtitle="Items that need action"
          theme={theme}
        />

        <Box
          sx={{
            mt: 2,
            width: "100%",
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(0, 1fr))",
              md: "repeat(3, minmax(0, 1fr))",
            },
            gap: 2,
          }}
        >
          {[
            {
              label: "Pending Leaves",
              value: hrPendingLeaves,
              icon: <FaUserClock />,
              color: "#d97706",
            },
            {
              label: "Low Attendance",
              value: 0,
              icon: (
                <FaCalendarCheck />
              ),
              color: "#ea580c",
            },
            {
              label:
                "Pending Expenses",
              value:
                pendingExpenses,
              icon: (
                <FaExclamationCircle />
              ),
              color: "#dc2626",
            },
          ].map((item) => (
            <Box
              key={item.label}
              sx={{
                width: "100%",
                minHeight: 105,
                p: 2.2,
                borderRadius: "14px",
                border: "1px solid",
                borderColor: alpha(
                  item.color,
                  0.25
                ),
                backgroundColor:
                  alpha(
                    item.color,
                    0.025
                  ),
                boxSizing:
                  "border-box",

                display: "flex",
                alignItems: "center",
                justifyContent:
                  "space-between",

                transition:
                  "all 0.2s ease",

                "&:hover": {
                  transform:
                    "translateY(-3px)",
                  borderColor:
                    alpha(
                      item.color,
                      0.6
                    ),
                  backgroundColor:
                    alpha(
                      item.color,
                      0.06
                    ),
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  minWidth: 0,
                }}
              >
                <Box
                  sx={{
                    width: 46,
                    height: 46,
                    minWidth: 46,
                    borderRadius:
                      "12px",
                    display: "flex",
                    alignItems:
                      "center",
                    justifyContent:
                      "center",
                    fontSize:
                      "1.1rem",
                    color:
                      item.color,
                    backgroundColor:
                      alpha(
                        item.color,
                        0.1
                      ),
                  }}
                >
                  {item.icon}
                </Box>

                <Box
                  sx={{
                    minWidth: 0,
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize:
                        "0.9rem",
                      color:
                        theme.palette.text
                          .primary,
                    }}
                  >
                    {item.label}
                  </Typography>

                  <Typography
                    sx={{
                      mt: 0.3,
                      fontSize:
                        "0.75rem",
                      color:
                        theme.palette.text
                          .disabled,
                    }}
                  >
                    Requires your attention
                  </Typography>
                </Box>
              </Box>

              <Box
                sx={{
                  minWidth: 42,
                  height: 42,
                  px: 1.2,
                  borderRadius:
                    "12px",
                  display: "flex",
                  alignItems:
                    "center",
                  justifyContent:
                    "center",
                  fontSize:
                    "1.1rem",
                  fontWeight: 800,
                  color:
                    item.color,
                  backgroundColor:
                    alpha(
                      item.color,
                      0.1
                    ),
                }}
              >
                {item.value}
              </Box>
            </Box>
          ))}
        </Box>
      </Paper>

   
{/* TODAY'S OPERATIONS */}

<Box sx={{ mb: 3, width: "100%" }}>
  <SectionHeader
    title="Today's Operations"
    subtitle="Quick operational overview"
    theme={theme}
  />

  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: {
        xs: "1fr",
        sm: "repeat(2, 1fr)",
        md: "repeat(4, 1fr)",
      },
      gap: 2.5,
      width: "100%",
    }}
  >
    {[
      {
        label: "Employees",
        value: hrActiveEmployees,
        subtitle: "Active",
        icon: <FaUsers />,
        color: "#2563eb",
        path: "/employee",
      },
      {
        label: "Enquiries",
        value: todayEnquiries,
        subtitle: "Today",
        icon: <FaPhone />,
        color: "#7c3aed",
        path: "/enquiry",
      },
      {
        label: "Follow Ups",
        value: totalFollowups,
        subtitle: "Pending",
        icon: <FaUserPlus />,
        color: "#0891b2",
        path: "/enquiry-follow-up",
      },
      {
        label: "Courses",
        value: totalCourses,
        subtitle: "Available",
        icon: <FaBook />,
        color: "#059669",
        path: "/course",
      },
    ].map((item) => (
      <Paper
        key={item.label}
        elevation={0}
        onClick={() => navigate(item.path)}
        sx={{
          width: "100%",
          minHeight: 145,
          p: 2.5,
          borderRadius: "14px",
          border: "1px solid",
          borderColor: theme.palette.divider,
          boxSizing: "border-box",
          backgroundColor: theme.palette.background.paper,
          cursor: "pointer",
          transition: "all 0.2s ease",

          "&:hover": {
            transform: "translateY(-3px)",
            borderColor: item.color,
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 10px 25px rgba(0,0,0,0.25)"
                : "0 10px 25px rgba(15,23,42,0.08)",
          },
        }}
      >
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: "11px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: item.color,
            backgroundColor: alpha(item.color, 0.1),
          }}
        >
          {item.icon}
        </Box>

        <Typography
          sx={{
            mt: 1.5,
            fontSize: "1.6rem",
            fontWeight: 800,
            color: theme.palette.text.primary,
          }}
        >
          {formatNumber(item.value)}
        </Typography>

        <Typography
          sx={{
            mt: 0.4,
            fontSize: "0.75rem",
            color: theme.palette.text.disabled,
          }}
        >
          {item.label} • {item.subtitle}
        </Typography>
      </Paper>
    ))}
  </Box>
</Box>


      {/* HR DASHBOARD */}
      {hrDashboardData && (
        <>
          <SectionHeader
            title="HR Overview"
            subtitle={`Employee, attendance and payroll summary • ${selectedMonthLabel}`}
            theme={theme}
          />

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(4, minmax(0, 1fr))" },
              gap: 2.5,
              mb: 3,
              width: "100%",
            }}
          >
            <KPICard
              title="Total Employees"
              value={formatNumber(hrTotalEmployees)}
              subtitle="Total HR employees"
              icon={<FaUsers size={17} />}
              color="#2563eb"
              theme={theme}
            />
            <KPICard
              title="Active Employees"
              value={formatNumber(hrActiveEmployees)}
              subtitle="Currently active"
              icon={<FaUserCheck size={17} />}
              color="#16a34a"
              theme={theme}
            />
            <KPICard
              title="Present Today"
              value={formatNumber(hrTodayPresent)}
              subtitle={`${hrTodayAttendancePercentage.toFixed(2)}% attendance`}
              icon={<FaCalendarCheck size={17} />}
              color="#7c3aed"
              theme={theme}
            />
            <KPICard
              title="Payroll"
              value={formatNumber(hrPayrollCount)}
              subtitle={`${hrPaidPayroll} paid • ${hrPendingPayroll} pending`}
              icon={<FaFileInvoice size={17} />}
              color="#ea580c"
              theme={theme}
            />
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
              gap: 2.5,
              mb: 3,
              width: "100%",
            }}
          >
            <Paper elevation={0} sx={{ p: 2.5, minHeight: 230, borderRadius: "16px", border: "1px solid", borderColor: theme.palette.divider, boxSizing: "border-box" }}>
              <SectionHeader title="HR Attendance" subtitle="Today and selected month" theme={theme} />
              {[
                ["Present Today", hrTodayPresent, "#16a34a", "number"],
                ["Absent Today", hrTodayAbsent, "#dc2626", "number"],
                ["Half Day Today", hrTodayHalfDay, "#f59e0b", "number"],
                ["On Leave Today", hrTodayOnLeave, "#2563eb", "number"],
                ["Today Attendance", `${hrTodayAttendancePercentage.toFixed(2)}%`, "#059669", "text"],
                ["Monthly Present", hrMonthPresent, "#7c3aed", "number"],
                ["Monthly Absent", hrMonthAbsent, "#dc2626", "number"],
                ["Monthly Half Day", hrMonthHalfDay, "#f59e0b", "number"],
                ["Monthly On Leave", hrMonthOnLeave, "#2563eb", "number"],
                ["Monthly Attendance", `${hrMonthAttendancePercentage.toFixed(2)}%`, "#0891b2", "text"],
              ].map(([label, value, color, valueType], index) => (
                <Box key={label} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1.5, borderBottom: index < 2 ? `1px solid ${theme.palette.divider}` : "none" }}>
                  <Typography sx={{ fontSize: "0.8rem", color: theme.palette.text.primary }}>{label}</Typography>
                  <Typography sx={{ fontSize: "1rem", fontWeight: 800, color }}>
                    {valueType === "number" ? formatNumber(value) : value}
                  </Typography>
                </Box>
              ))}
            </Paper>

            <Paper elevation={0} sx={{ p: 2.5, minHeight: 230, borderRadius: "16px", border: "1px solid", borderColor: theme.palette.divider, boxSizing: "border-box" }}>
              <SectionHeader title="HR Payroll" subtitle="Payroll processing status" theme={theme} />
              {[
                ["Total Payroll", hrPayrollCount, "#2563eb", "number"],
                ["Paid Payroll", hrPaidPayroll, "#16a34a", "number"],
                ["Pending Payroll", hrPendingPayroll, "#f59e0b", "number"],
                ["Gross Salary", hrGrossSalary, "#7c3aed", "currency"],
                ["Deductions", hrDeductions, "#dc2626", "currency"],
                ["Attendance Deduction", hrAttendanceDeduction, "#ea580c", "currency"],
                ["Net Salary", hrNetSalary, "#059669", "currency"],
              ].map(([label, value, color, valueType], index) => (
                <Box key={label} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1.25, borderBottom: index < 6 ? `1px solid ${theme.palette.divider}` : "none" }}>
                  <Typography sx={{ fontSize: "0.8rem", color: theme.palette.text.primary }}>{label}</Typography>
                  <Typography sx={{ fontSize: "1rem", fontWeight: 800, color }}>
                    {valueType === "currency" ? formatCurrency(value) : formatNumber(value)}
                  </Typography>
                </Box>
              ))}
            </Paper>
          </Box>
        </>
      )}

      {/* HR DETAILS */}
      {hrDashboardData && (
        <>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                lg: "repeat(2, minmax(0, 1fr))",
              },
              gap: 2.5,
              mb: 3,
              width: "100%",
            }}
          >
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                minHeight: 320,
                borderRadius: "16px",
                border: "1px solid",
                borderColor: theme.palette.divider,
                boxSizing: "border-box",
              }}
            >
              <SectionHeader
                title="Department Summary"
                subtitle="Employees by department"
                theme={theme}
              />

              {hrDepartmentData.length ? (
                hrDepartmentData.map((item, index) => (
                  <Box
                    key={`${item.name}-${index}`}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      py: 1.5,
                      borderBottom:
                        index < hrDepartmentData.length - 1
                          ? `1px solid ${theme.palette.divider}`
                          : "none",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.85rem",
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                        textTransform: "capitalize",
                      }}
                    >
                      {item.name}
                    </Typography>
                    <Chip
                      size="small"
                      label={formatNumber(item.count)}
                      sx={{
                        fontWeight: 800,
                        color: theme.palette.primary.contrastText,
                        backgroundColor: theme.palette.primary.main,
                      }}
                    />
                  </Box>
                ))
              ) : (
                <Typography
                  sx={{
                    color: theme.palette.text.primary,
                  }}
                >
                  No department data available.
                </Typography>
              )}
            </Paper>

            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                minHeight: 320,
                borderRadius: "16px",
                border: "1px solid",
                borderColor: theme.palette.divider,
                boxSizing: "border-box",
              }}
            >
              <SectionHeader
                title="Designation Summary"
                subtitle="Employees by designation"
                theme={theme}
              />

              {hrDesignationData.length ? (
                hrDesignationData.map((item, index) => (
                  <Box
                    key={`${item.name}-${index}`}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      py: 1.5,
                      borderBottom:
                        index < hrDesignationData.length - 1
                          ? `1px solid ${theme.palette.divider}`
                          : "none",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.85rem",
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                        textTransform: "capitalize",
                      }}
                    >
                      {item.name}
                    </Typography>
                    <Chip
                      size="small"
                      label={formatNumber(item.count)}
                      sx={{
                        fontWeight: 800,
                        color: theme.palette.primary.contrastText,
                        backgroundColor: theme.palette.primary.main,
                      }}
                    />
                  </Box>
                ))
              ) : (
                <Typography
                  sx={{
                    color: theme.palette.text.primary,
                  }}
                >
                  No designation data available.
                </Typography>
              )}
            </Paper>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                lg: "repeat(2, minmax(0, 1fr))",
              },
              gap: 2.5,
              mb: 3,
              width: "100%",
            }}
          >
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                height: 400,
                borderRadius: "16px",
                border: "1px solid",
                borderColor: theme.palette.divider,
                boxSizing: "border-box",
              }}
            >
              <SectionHeader
                title="Monthly Payroll Trend"
                subtitle="Gross, deductions and net salary"
                theme={theme}
              />
              <Box sx={{ height: 300, width: "100%" }}>
                {hrMonthlyPayrollTrend.length ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={hrMonthlyPayrollTrend}>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        vertical={false}
                        stroke={theme.palette.divider}
                      />
                      <XAxis
                        dataKey="month"
                        axisLine={false}
                        tickLine={false}
                        tick={{
                          fill:
                            theme.palette.mode === "dark"
                              ? "#FFFFFF"
                              : "#111827",
                        }}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tick={{
                          fill:
                            theme.palette.mode === "dark"
                              ? "#FFFFFF"
                              : "#111827",
                        }}
                        tickFormatter={(value) =>
                          value >= 1000
                            ? `₹${(value / 1000).toFixed(0)}k`
                            : `₹${value}`
                        }
                      />
                      <Tooltip content={<ChartTooltip />} />
                      <Line
                        type="monotone"
                        dataKey="gross"
                        name="Gross Salary"
                        stroke="#2563eb"
                        strokeWidth={3}
                        dot={{ r: 4 }}
                      />
                      <Line
                        type="monotone"
                        dataKey="deductions"
                        name="Deductions"
                        stroke="#dc2626"
                        strokeWidth={3}
                        dot={{ r: 4 }}
                      />
                      <Line
                        type="monotone"
                        dataKey="net"
                        name="Net Salary"
                        stroke="#059669"
                        strokeWidth={3}
                        dot={{ r: 4 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <Box
                    sx={{
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        color: theme.palette.text.primary,
                      }}
                    >
                      No payroll trend data available.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                height: 400,
                borderRadius: "16px",
                border: "1px solid",
                borderColor: theme.palette.divider,
                boxSizing: "border-box",
              }}
            >
              <SectionHeader
                title="Monthly Attendance Trend"
                subtitle="Present, absent, half day and leave"
                theme={theme}
              />
              <Box sx={{ height: 300, width: "100%" }}>
                {hrMonthlyAttendanceTrend.length ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={hrMonthlyAttendanceTrend}>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        vertical={false}
                        stroke={theme.palette.divider}
                      />
                      <XAxis
                        dataKey="month"
                        axisLine={false}
                        tickLine={false}
                        tick={{
                          fill:
                            theme.palette.mode === "dark"
                              ? "#FFFFFF"
                              : "#111827",
                        }}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tick={{
                          fill:
                            theme.palette.mode === "dark"
                              ? "#FFFFFF"
                              : "#111827",
                        }}
                      />
                      <Tooltip content={<ChartTooltip />} />
                      <Line
                        type="monotone"
                        dataKey="present"
                        name="Present"
                        stroke="#16a34a"
                        strokeWidth={3}
                        dot={{ r: 4 }}
                      />
                      <Line
                        type="monotone"
                        dataKey="absent"
                        name="Absent"
                        stroke="#dc2626"
                        strokeWidth={3}
                        dot={{ r: 4 }}
                      />
                      <Line
                        type="monotone"
                        dataKey="halfDay"
                        name="Half Day"
                        stroke="#f59e0b"
                        strokeWidth={3}
                        dot={{ r: 4 }}
                      />
                      <Line
                        type="monotone"
                        dataKey="onLeave"
                        name="On Leave"
                        stroke="#2563eb"
                        strokeWidth={3}
                        dot={{ r: 4 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <Box
                    sx={{
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        color: theme.palette.text.primary,
                      }}
                    >
                      No attendance trend data available.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          </Box>

      
<Box
  sx={{
    display: "grid",
    gridTemplateColumns: {
      xs: "1fr",
      lg: "repeat(2, minmax(0, 1fr))",
    },
    gap: 2.5,
    mb: 3,
    width: "100%",
  }}
>
  <Paper
    elevation={0}
    sx={{
      p: 2.5,
      minHeight: 360,
      borderRadius: "16px",
      border: "1px solid",
      borderColor: theme.palette.divider,
      boxSizing: "border-box",
    }}
  >
    <SectionHeader
      title="Recent Leave Applications"
      subtitle="Latest employee leave requests"
      theme={theme}
    />

    <TableContainer>
      <Table
        size="small"
        sx={{
          "& .MuiTableCell-root": {
            color: theme.palette.text.primary,
          },
          "& .MuiTableHead-root .MuiTableCell-root": {
            color: "#ffffff",
            backgroundColor: theme.palette.primary.main,
            fontWeight: 800,
            whiteSpace: "nowrap",
          },
        }}
      >
        <TableHead>
          <TableRow>
            {["S.No", "Employee", "Leave", "Dates", "Days", "Status"].map(
              (head) => (
                <TableCell
                  key={head}
                  sx={{
                    fontWeight: 800,
                    color: "#ffffff",
                    backgroundColor: theme.palette.primary.main,
                    whiteSpace: "nowrap",
                  }}
                >
                  {head}
                </TableCell>
              )
            )}
          </TableRow>
        </TableHead>

        <TableBody>
          {hrRecentLeaveApplications.length ? (
            hrRecentLeaveApplications.map((item, index) => (
              <TableRow key={item.id}>
                <TableCell>{index + 1}</TableCell>

                <TableCell>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {item.employee_name}
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: "0.7rem",
                      color: theme.palette.text.secondary,
                    }}
                  >
                    {item.employee_code}
                  </Typography>
                </TableCell>

                <TableCell>{item.leave_type_name}</TableCell>

                <TableCell>
                  {item.from_date} → {item.to_date}
                </TableCell>

                <TableCell>{formatNumber(item.total_days)}</TableCell>

                <TableCell>
                  <Chip
                    size="small"
                    label={item.status}
                    sx={{
                      textTransform: "capitalize",
                      fontWeight: 700,
                    }}
                  />
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={6} align="center">
                No leave applications available.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  </Paper>

  <Paper
    elevation={0}
    sx={{
      p: 2.5,
      minHeight: 360,
      borderRadius: "16px",
      border: "1px solid",
      borderColor: theme.palette.divider,
      boxSizing: "border-box",
    }}
  >
    <SectionHeader
      title="Recent Employees"
      subtitle="Employees returned by HR dashboard API"
      theme={theme}
    />

    <TableContainer>
      <Table
        size="small"
        sx={{
          "& .MuiTableCell-root": {
            color: theme.palette.text.primary,
          },
          "& .MuiTableHead-root .MuiTableCell-root": {
            color: "#ffffff",
            backgroundColor: theme.palette.primary.main,
            fontWeight: 800,
            whiteSpace: "nowrap",
          },
        }}
      >
        <TableHead>
          <TableRow>
            {[
              "S.No",
              "Employee",
              "Department",
              "Designation",
              "Joining",
              "Status",
            ].map((head) => (
              <TableCell
                key={head}
                sx={{
                  fontWeight: 800,
                  color: "#ffffff",
                  backgroundColor: theme.palette.primary.main,
                  whiteSpace: "nowrap",
                }}
              >
                {head}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {hrRecentEmployees.length ? (
            hrRecentEmployees.map((item, index) => (
              <TableRow key={item.id}>
                <TableCell>{index + 1}</TableCell>

                <TableCell>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {item.employee_name}
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: "0.7rem",
                      color: theme.palette.text.secondary,
                    }}
                  >
                    {item.employee_code}
                  </Typography>
                </TableCell>

                <TableCell sx={{ textTransform: "capitalize" }}>
                  {item.department_name}
                </TableCell>

                <TableCell sx={{ textTransform: "capitalize" }}>
                  {item.designation_name}
                </TableCell>

                <TableCell>{item.date_of_joining}</TableCell>

                <TableCell>
                  <Chip
                    size="small"
                    label={item.is_active ? "Active" : "Inactive"}
                    sx={{ fontWeight: 700 }}
                  />
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={6} align="center">
                No employee data available.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  </Paper>
</Box>

        </>
      )}

      {/* FINANCIAL TREND */}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            lg: "minmax(0, 2fr) minmax(300px, 1fr)",
          },
          gap: 2.5,
          mb: 3,
          width: "100%",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            height: 400,
            borderRadius: "16px",
            border: "1px solid",
            borderColor:
              theme.palette.divider,
            boxSizing: "border-box",
          }}
        >
          <SectionHeader
            title="Collection vs Expense Trend"
            subtitle={`Financial performance overview • ${selectedMonthLabel}`}
            theme={theme}
          />

          <Box
            sx={{
              height: 300,
              width: "100%",
            }}
          >
            <ResponsiveContainer
              width="100%"
              height="100%"
            >
              <LineChart
                data={financialTrendData}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  stroke={
                    theme.palette.divider
                  }
                />

                <XAxis
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  tick={{
                    fill:
                      theme.palette.mode === "dark"
                        ? "#FFFFFF"
                        : "#111827",
                  }}
                />

                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{
                    fill:
                      theme.palette.mode === "dark"
                        ? "#FFFFFF"
                        : "#111827",
                  }}
                  tickFormatter={(value) =>
                    value >= 1000
                      ? `₹${(
                          value / 1000
                        ).toFixed(0)}k`
                      : `₹${value}`
                  }
                />

                <Tooltip
                  content={
                    <ChartTooltip />
                  }
                />

                <Line
                  type="monotone"
                  dataKey="collection"
                  name="Collection"
                  stroke="#059669"
                  strokeWidth={3}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                />

                <Line
                  type="monotone"
                  dataKey="expense"
                  name="Expense"
                  stroke="#ea580c"
                  strokeWidth={3}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            height: 400,
            borderRadius: "16px",
            border: "1px solid",
            borderColor:
              theme.palette.divider,
            boxSizing: "border-box",
          }}
        >
          <SectionHeader
            title="Financial Summary"
            subtitle="Current overview"
            theme={theme}
          />

          {[
            {
              label: "Collection",
              value: totalCollection,
              color: "#059669",
            },
            {
              label: "Expense",
              value: totalExpense,
              color: "#ea580c",
            },
            {
              label: "Net Balance",
              value: netBalance,
              color: "#2563eb",
            },
          ].map(
            (item, index) => (
              <Box
                key={item.label}
                sx={{
                  py: 2.5,
                  borderBottom:
                    index !== 2
                      ? `1px solid ${theme.palette.divider}`
                      : "none",
                  display: "flex",
                  justifyContent:
                    "space-between",
                  alignItems: "center",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 9,
                      height: 9,
                      borderRadius: "50%",
                      backgroundColor:
                        item.color,
                    }}
                  />

                  <Typography
                    sx={{
                      fontWeight: 600,
                      color:
                        theme.palette.text
                          .disabled,
                    }}
                  >
                    {item.label}
                  </Typography>
                </Box>

                <Typography
                  sx={{
                    fontSize: "1.1rem",
                    fontWeight: 800,
                    color: item.color,
                  }}
                >
                  {formatCurrency(
                    item.value
                  )}
                </Typography>
              </Box>
            )
          )}
        </Paper>
      </Box>

      {/* ATTENDANCE + LEAVE */}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "repeat(2, minmax(0, 1fr))",
          },
          gap: 2.5,
          mb: 3,
          width: "100%",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            minHeight: 330,
            borderRadius: "16px",
            border: "1px solid",
            borderColor:
              theme.palette.divider,
            boxSizing: "border-box",
          }}
        >
          <SectionHeader
            title="Attendance Overview"
            subtitle="Overall attendance performance"
            theme={theme}
          />

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mt: 1,
            }}
          >
            <Box
              sx={{
                width: 145,
                height: 145,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: `conic-gradient(
                  #059669 ${
                    hrMonthAttendancePercentage * 3.6
                  }deg,
                  ${alpha(
                    "#059669",
                    0.12
                  )} ${
                  hrMonthAttendancePercentage * 3.6
                }deg
                360deg
              )`,
                p: "10px",
                boxShadow:
                  theme.palette.mode === "dark"
                    ? "inset 0 0 20px rgba(0,0,0,0.25)"
                    : "inset 0 0 20px rgba(15,23,42,0.04)",
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  borderRadius: "50%",
                  backgroundColor:
                    theme.palette
                      .background.paper,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "1.5rem",
                    fontWeight: 800,
                    color: "#059669",
                    lineHeight: 1,
                  }}
                >
                  {hrMonthAttendancePercentage.toFixed(2)}%
                </Typography>

                <Typography
                  sx={{
                    mt: 0.7,
                    fontSize: "0.65rem",
                    fontWeight: 800,
                    letterSpacing: "0.06em",
                    color:
                      theme.palette.text
                        .disabled,
                  }}
                >
                  ATTENDANCE
                </Typography>
              </Box>
            </Box>

            <Typography
              sx={{
                mt: 1.5,
                fontWeight: 700,
              }}
            >
              Overall Attendance
            </Typography>

            <Box
              sx={{
                mt: 1.5,
                display: "flex",
                gap: 1,
                flexWrap: "wrap",
                justifyContent: "center",
              }}
            >
              <Box
                sx={{
                  px: 1.2,
                  py: 0.65,
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.6,
                  backgroundColor: alpha(
                    "#16a34a",
                    0.1
                  ),
                  color: "#15803d",
                  fontSize: "0.72rem",
                  fontWeight: 700,
                }}
              >
                <FaCheck size={10} />

                Present{" "}
                {formatNumber(
                  hrMonthPresent
                )}
              </Box>

              <Box
                sx={{
                  px: 1.2,
                  py: 0.65,
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.6,
                  backgroundColor: alpha(
                    "#dc2626",
                    0.08
                  ),
                  color: "#dc2626",
                  fontSize: "0.72rem",
                  fontWeight: 700,
                }}
              >
                <FaTimes size={10} />

                Absent{" "}
                {formatNumber(
                  hrMonthAbsent
                )}
              </Box>
            </Box>
          </Box>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            minHeight: 330,
            borderRadius: "16px",
            border: "1px solid",
            borderColor:
              theme.palette.divider,
            boxSizing: "border-box",
          }}
        >
          <SectionHeader
            title="Leave Overview"
            subtitle="Employee leave status"
            theme={theme}
          />

          {[
            {
              label: "Pending",
              value: hrPendingLeaves,
              color: "#d97706",
            },
            {
              label: "Approved",
              value: hrApprovedLeaves,
              color: "#059669",
            },
            {
              label: "On Leave",
              value: hrTodayOnLeave,
              color: "#2563eb",
            },
          ].map(
            (item, index) => (
              <Box
                key={item.label}
                sx={{
                  display: "flex",
                  justifyContent:
                    "space-between",
                  alignItems: "center",
                  py: 2.2,
                  borderBottom:
                    index !== 2
                      ? `1px solid ${theme.palette.divider}`
                      : "none",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 9,
                      height: 9,
                      borderRadius: "50%",
                      backgroundColor:
                        item.color,
                    }}
                  />

                  <Typography
                    sx={{
                      fontWeight: 600,
                    }}
                  >
                    {item.label}
                  </Typography>
                </Box>

                <Typography
                  sx={{
                    fontSize: "1.2rem",
                    fontWeight: 800,
                    color: item.color,
                  }}
                >
                  {item.value}
                </Typography>
              </Box>
            )
          )}

          <Typography
            sx={{
              mt: 2,
              textAlign: "center",
              fontSize: "0.75rem",
              color:
                theme.palette.text.primary,
            }}
          >
            Rejected: {formatNumber(hrRejectedLeaves)} •
            Cancelled: {formatNumber(hrCancelledLeaves)}
          </Typography>
        </Paper>
      </Box>

      {/* EXPENSE BREAKDOWN */}

      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          width: "100%",
          mb: 3,
          borderRadius: "16px",
          border: "1px solid",
          borderColor:
            theme.palette.divider,
          backgroundColor:
            theme.palette.background.paper,
          boxSizing: "border-box",
        }}
      >
        <SectionHeader
          title="Expense Breakdown"
          subtitle="Expense distribution by category"
          theme={theme}
        />

        {expenseFallbackData.length >
        0 ? (
          <Box
            sx={{
              width: "100%",
              height: 340,
              mt: 2,
            }}
          >
            <ResponsiveContainer
              width="100%"
              height="100%"
            >
              <BarChart
                data={expenseFallbackData}
                margin={{
                  top: 20,
                  right: 30,
                  left: 10,
                  bottom: 15,
                }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  stroke={
                    theme.palette.divider
                  }
                />

                <XAxis
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  interval={0}
                  tick={{
                    fontSize: 11,
                    fill:
                      theme.palette.mode ===
                      "dark"
                        ? "#cbd5e1"
                        : "#64748b",
                  }}
                  tickFormatter={(value) =>
                    truncateText(
                      value,
                      16
                    )
                  }
                />

                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{
                    fontSize: 11,
                    fill:
                      theme.palette.mode ===
                      "dark"
                        ? "#cbd5e1"
                        : "#64748b",
                  }}
                  tickFormatter={(value) =>
                    value >= 1000
                      ? `₹${
                          value / 1000
                        }k`
                      : `₹${value}`
                  }
                />

           <Tooltip
  cursor={{
    fill:
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.04)"
        : "rgba(0,0,0,0.03)",
  }}
  formatter={(value) => formatCurrency(value)}
  labelFormatter={(label) => label}
  contentStyle={{
    borderRadius: "10px",
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
  }}
  labelStyle={{
    color: theme.palette.text.primary,
    fontWeight: 600,
  }}
  itemStyle={{
    color: theme.palette.text.primary,
  }}
/>

                <Bar
                  dataKey="value"
                  name="Expense"
                  radius={[
                    8,
                    8,
                    0,
                    0,
                  ]}
                  maxBarSize={100}
                >
                  {expenseFallbackData.map(
                    (_, index) => (
                      <Cell
                        key={index}
                        fill={[
                          "#6366f1",
                          "#059669",
                          "#f59e0b",
                          "#2563eb",
                          "#ec4899",
                        ][index % 5]}
                      />
                    )
                  )}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Box>
        ) : (
          <Box
            sx={{
              height: 320,
              display: "flex",
              alignItems: "center",
              justifyContent:
                "center",
              flexDirection:
                "column",
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: "14px",
                display: "flex",
                alignItems: "center",
                justifyContent:
                  "center",
                backgroundColor:
                  alpha(
                    theme.palette
                      .primary.main,
                    0.08
                  ),
                color:
                  theme.palette.primary.main,
              }}
            >
              <FaChartBar size={25} />
            </Box>

            <Typography
              sx={{
                fontSize: "0.85rem",
                color:
                  theme.palette.text
                    .disabled,
              }}
            >
              No expense data available
            </Typography>
          </Box>
        )}
      </Paper>

      {/* QUICK ACTIONS */}

      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          width: "100%",
          mb: 3,
          borderRadius: "16px",
          border: "1px solid",
          borderColor:
            theme.palette.divider,
          backgroundColor:
            theme.palette.background.paper,
          boxSizing: "border-box",
        }}
      >
        <SectionHeader
          title="Quick Actions"
          subtitle="Frequently used actions"
          theme={theme}
        />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(2, 1fr)",
              sm: "repeat(3, 1fr)",
              md: "repeat(6, 1fr)",
            },
            gap: 2,
            width: "100%",
            mt: 2,
          }}
        >
          {[
            {
              label: "Add Student",
              icon: (
                <FaGraduationCap
                  size={21}
                />
              ),
              color: "#2563eb",
              path: "/add-admission",
            },
            {
              label: "Add Employee",
              icon: (
                <FaUserTie size={21} />
              ),
              color: "#7c3aed",
              path: "/add-employee",
            },
            {
              label: "Add Expense",
              icon: (
                <FaMoneyBill
                  size={21}
                />
              ),
              color: "#ea580c",
              path:
                "/expense-management/expense-entry",
            },
            {
              label: "Apply Leave",
              icon: (
                <FaCalendarCheck
                  size={21}
                />
              ),
              color: "#059669",
              path: "/leave-apply",
            },
            {
              label: "Payroll",
              icon: (
                <FaWallet size={21} />
              ),
              color: "#0891b2",
              path: "/salary-head",
            },
            {
              label: "View Reports",
              icon: (
                <FaFileInvoice
                  size={21}
                />
              ),
              color: "#6366f1",
              path:
                "/report/expense-report",
            },
          ].map((action) => (
            <Button
              key={action.label}
              onClick={() =>
                navigate(action.path)
              }
              sx={{
                width: "100%",
                minHeight: 125,
                borderRadius: "14px",
                border: "1px solid",
                borderColor: alpha(
                  action.color,
                  0.28
                ),
                color: action.color,
                backgroundColor:
                  alpha(
                    action.color,
                    0.035
                  ),

                display: "flex",
                flexDirection:
                  "column",
                alignItems: "center",
                justifyContent:
                  "center",

                gap: 1.3,
                textTransform: "none",
                fontSize: "0.82rem",
                fontWeight: 700,

                transition:
                  "all 0.2s ease",

                "&:hover": {
                  transform:
                    "translateY(-4px)",
                  backgroundColor:
                    alpha(
                      action.color,
                      0.09
                    ),
                  borderColor:
                    action.color,
                  boxShadow:
                    theme.palette.mode ===
                    "dark"
                      ? "0 10px 25px rgba(0,0,0,0.3)"
                      : "0 10px 25px rgba(15,23,42,0.1)",
                },
              }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "13px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent:
                    "center",
                  color: action.color,
                  backgroundColor:
                    alpha(
                      action.color,
                      0.12
                    ),
                }}
              >
                {action.icon}
              </Box>

              <Typography
                sx={{
                  fontSize: "0.82rem",
                  fontWeight: 700,
                  textAlign: "center",
                  color: "inherit",
                }}
              >
                {action.label}
              </Typography>
            </Button>
          ))}
        </Box>
      </Paper>

      {/* ======================================================
          BRANCH PERFORMANCE
      ====================================================== */}

      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 3,
          borderRadius: "16px",
          border: "1px solid",
          borderColor:
            theme.palette.divider,
        }}
      >
        <SectionHeader
          title="Branch Performance"
          subtitle={
            "Expense overview for the selected branch"
          }
          theme={theme}
        />

        {branchPerformance.length >
        0 ? (
          <TableContainer>
            <Table
              sx={{
                minWidth: 850,
              }}
            >
              <TableHead>
                <TableRow>
                  {[
                    "S.No",
                    "Branch",
                    "Total Expenses",
                    "Approved Amount",
                    "Pending",
                    "Top Category",
                    "Status",
                  ].map(
                    (heading) => (
                      <TableCell
                        key={heading}
                        sx={{
                          fontSize:
                            "0.7rem",
                          fontWeight: 800,
                          textTransform:
                            "uppercase",
                          color:
                            theme.palette.text
                              .disabled,
                        }}
                      >
                        {heading}
                      </TableCell>
                    )
                  )}
                </TableRow>
              </TableHead>

              <TableBody>
                {branchPerformance.map(
                  (branch, index) => (
                    <TableRow
                      key={
                        branch.branch_id ||
                        index
                      }
                      hover
                    >
                      <TableCell>{index + 1}</TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                        }}
                      >
                        <Box
                          sx={{
                            display:
                              "flex",
                            alignItems:
                              "center",
                            gap: 1,
                          }}
                        >
                          <FaBuilding
                            size={13}
                          />

                          {
                            branch.branch_name
                          }
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Typography
                          sx={{
                            fontWeight: 700,
                          }}
                        >
                          {formatNumber(
                            branch.total_expenses
                          )}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            color:
                              "#059669",
                          }}
                        >
                          {formatCurrency(
                            branch.approved_amount
                          )}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={`${branch.pending} Pending`}
                          size="small"
                          sx={{
                            fontWeight: 700,
                            color:
                              branch.pending >
                              0
                                ? "#dc2626"
                                : "#15803d",
                            backgroundColor:
                              alpha(
                                branch.pending >
                                  0
                                  ? "#dc2626"
                                  : "#16a34a",
                                0.1
                              ),
                          }}
                        />
                      </TableCell>

                      <TableCell>
                        <MuiTooltip
                          title={
                            branch.top_category
                          }
                        >
                          <Typography
                            sx={{
                              maxWidth: 190,
                              overflow:
                                "hidden",
                              textOverflow:
                                "ellipsis",
                              whiteSpace:
                                "nowrap",
                              fontWeight: 600,
                            }}
                          >
                            {
                              branch.top_category
                            }
                          </Typography>
                        </MuiTooltip>
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={
                            branch.status
                          }
                          size="small"
                          sx={{
                            fontWeight: 700,
                            color:
                              "#15803d",
                            backgroundColor:
                              alpha(
                                "#16a34a",
                                0.1
                              ),
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  )
                )}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box
            sx={{
              py: 5,
              textAlign: "center",
            }}
          >
            <Box
              sx={{
                width: 55,
                height: 55,
                mx: "auto",
                mb: 1.5,
                borderRadius: "14px",
                display: "flex",
                alignItems: "center",
                justifyContent:
                  "center",
                color:
                  theme.palette.primary.main,
                backgroundColor:
                  alpha(
                    theme.palette
                      .primary.main,
                    0.08
                  ),
              }}
            >
              <FaBuilding size={22} />
            </Box>

            <Typography
              sx={{
                fontWeight: 700,
              }}
            >
              No branch data available
            </Typography>

            <Typography
              sx={{
                mt: 0.5,
                fontSize: "0.75rem",
                color:
                  theme.palette.text
                    .disabled,
              }}
            >
              Select a branch to view
              detailed performance
            </Typography>
          </Box>
        )}
      </Paper>

      {/* RECENT ACTIVITY */}

      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: "16px",
          border: "1px solid",
          borderColor:
            theme.palette.divider,
        }}
      >
        <SectionHeader
          title="Recent Activity"
          subtitle="Latest operational updates"
          theme={theme}
        />

        {activities.map(
          (activity, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                alignItems:
                  "center",
                justifyContent:
                  "space-between",
                gap: 2,
                py: 1.7,
                borderBottom:
                  index !==
                  activities.length - 1
                    ? `1px solid ${theme.palette.divider}`
                    : "none",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems:
                    "center",
                  gap: 1.5,
                }}
              >
                <Box
                  sx={{
                    width: 38,
                    height: 38,
                    borderRadius:
                      "10px",
                    display:
                      "flex",
                    alignItems:
                      "center",
                    justifyContent:
                      "center",
                    color:
                      activity.color,
                    backgroundColor:
                      alpha(
                        activity.color,
                        0.1
                      ),
                  }}
                >
                  {activity.icon}
                </Box>

                <Box>
                  <Typography
                    sx={{
                      fontSize:
                        "0.82rem",
                      fontWeight: 700,
                    }}
                  >
                    {activity.title}
                  </Typography>

                  <Typography
                    sx={{
                      mt: 0.3,
                      fontSize:
                        "0.72rem",
                      color:
                        theme.palette.text
                          .disabled,
                    }}
                  >
                    {
                      activity.description
                    }
                  </Typography>
                </Box>
              </Box>

              <Typography
                sx={{
                  fontSize:
                    "0.72rem",
                  color:
                    theme.palette.text
                      .disabled,
                  whiteSpace:
                    "nowrap",
                }}
              >
                {activity.time}
              </Typography>
            </Box>
          )
        )}
      </Paper>
    </Box>
  );
};

export default Dashboard;